import React from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  CreditCard, 
  Smartphone, 
  CheckCircle, 
  TrendingUp, 
  Shield, 
  Zap, 
  DollarSign, 
  Search, 
  Settings, 
  MoreHorizontal, 
  Filter, 
  Calendar, 
  ChevronDown,
  ArrowRight,
  PieChart,
  BarChart3,
  Layers
} from 'lucide-react';

const PixIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 12L12 22L22 12L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 7L7 12L12 17L17 12L12 7Z" fill="currentColor"/>
  </svg>
);

export const OperationalDashboardMockup = () => {
  const stats = [
    { label: 'Total Vendido', value: 'R$ 93.061,83', icon: DollarSign, color: 'bg-emerald-500' },
    { label: 'Qtde. Cupons', value: '1.189', icon: CreditCard, color: 'bg-orange-500' },
    { label: 'Ticket Médio', value: 'R$ 78,27', icon: TrendingUp, color: 'bg-purple-500' },
    { label: 'Lucro Líquido', value: 'R$ 16.447,73', icon: Zap, color: 'bg-emerald-600' },
  ];

  return (
    <div className="les-mockup-container" style={{ background: 'transparent', width: '100%', maxWidth: '900px', padding: '10px' }}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-50 rounded-[2rem] shadow-2xl border border-white overflow-hidden flex h-[500px]"
      >
        {/* Sidebar */}
        <div className="w-48 bg-[#1a1a1a] flex flex-col p-4">
          <div className="bg-[#7f00ff] p-3 rounded-xl mb-8 flex items-center gap-2">
            <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center">
              <Zap size={14} className="text-white" fill="white" />
            </div>
            <span className="text-[10px] text-white font-black uppercase tracking-tighter">Dashboard</span>
          </div>
          <div className="space-y-4">
            {['Vendas', 'Metas', 'Financeiro', 'Combustível', 'Estoque'].map((item, i) => (
              <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[10px] font-bold ${i === 0 ? 'bg-white/10 text-white' : 'text-white/40'}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="h-14 bg-white border-b border-slate-100 px-6 flex items-center justify-between">
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Painel Operacional</span>
            <div className="flex items-center gap-4 text-[10px] font-black text-slate-600">
               <span>TODAS UNIDADES</span>
               <div className="w-8 h-8 rounded-full bg-slate-100" />
            </div>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto">
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4">
              {stats.map((s, i) => (
                <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-start gap-3">
                  <div className={`p-2 ${s.color} rounded-xl text-white`}>
                    <s.icon size={16} />
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase leading-none mb-1">{s.label}</p>
                    <h5 className="text-[13px] font-black text-slate-900 tracking-tight">{s.value}</h5>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts Area */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-[1.5rem] shadow-sm border border-slate-100 h-56 flex flex-col">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black text-slate-400 uppercase">Vendas Última Hora (R$)</span>
                  <div className="flex gap-1">
                     <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  </div>
                </div>
                <div className="flex-1 flex items-end gap-1.5 px-2">
                  {[40, 70, 45, 90, 65, 80, 55, 100, 75, 60, 85].map((h, i) => (
                    <motion.div 
                      key={i}
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      transition={{ delay: i * 0.05, duration: 0.8 }}
                      className="flex-1 bg-emerald-500/20 border-t-2 border-emerald-500 rounded-t-sm"
                    />
                  ))}
                </div>
              </div>

              <div className="bg-white p-5 rounded-[1.5rem] shadow-sm border border-slate-100 h-56 flex flex-col">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-[10px] font-black text-slate-400 uppercase">Fluxo de Cupons</span>
                  <div className="flex gap-1">
                     <div className="w-2 h-2 rounded-full bg-blue-400" />
                  </div>
                </div>
                <div className="flex-1 relative flex items-center justify-center">
                  <svg className="w-full h-full overflow-visible">
                    <motion.path
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 2 }}
                      d="M 0 100 Q 25 20, 50 80 T 100 40 T 150 90 T 200 10 T 250 70"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="3"
                      className="w-full h-full"
                    />
                  </svg>
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-50/50 to-transparent pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const ReconciliationMockup = () => {

  const [activeTab, setActiveTab] = React.useState<'Pendente' | 'Conciliado'>('Pendente');
  const [activeAcquirer, setActiveAcquirer] = React.useState('Todos');
  const [filterOpen, setFilterOpen] = React.useState(false);

  type Tx = { id: string; source: string; acquirer: string; date: string; value: number; feeRaw: number; fee: string; status: 'Pendente' | 'Conciliado'; icon: 'pix' | 'card' | 'money' };
  const allTransactions: Tx[] = [
    { id: 'TX-9042', source: 'Stone — Débito',    acquirer: 'Stone',  date: '22 Abr', value: 450.00,   feeRaw: 1.8,  fee: '1.8%',  status: 'Pendente',   icon: 'card'  },
    { id: 'TX-9039', source: 'Pix — App',         acquirer: 'Stone',  date: '21 Abr', value: 2150.00,  feeRaw: 0,    fee: '0%',    status: 'Pendente',   icon: 'pix'   },
    { id: 'TX-9011', source: 'Cielo — Crédito',   acquirer: 'Cielo',  date: '20 Abr', value: 890.00,   feeRaw: 2.1,  fee: '2.1%',  status: 'Pendente',   icon: 'card'  },
    { id: 'TX-8998', source: 'Getnet — Débito',   acquirer: 'Getnet', date: '19 Abr', value: 310.00,   feeRaw: 1.6,  fee: '1.6%',  status: 'Pendente',   icon: 'card'  },
    { id: 'TX-8990', source: 'Stone — Crédito',   acquirer: 'Stone',  date: '18 Abr', value: 1200.00,  feeRaw: 2.5,  fee: '2.5%',  status: 'Conciliado', icon: 'card'  },
    { id: 'TX-8982', source: 'Cielo — Débito',    acquirer: 'Cielo',  date: '17 Abr', value: 540.00,   feeRaw: 1.5,  fee: '1.5%',  status: 'Conciliado', icon: 'card'  },
    { id: 'TX-8975', source: 'Pix — QR Code',     acquirer: 'Stone',  date: '16 Abr', value: 3200.00,  feeRaw: 0,    fee: '0%',    status: 'Conciliado', icon: 'pix'   },
    { id: 'TX-8960', source: 'Getnet — Crédito',  acquirer: 'Getnet', date: '15 Abr', value: 760.00,   feeRaw: 2.2,  fee: '2.2%',  status: 'Conciliado', icon: 'card'  },
  ];

  const acquirers = ['Todos', 'Stone', 'Cielo', 'Getnet'];

  const filtered = allTransactions.filter(tx =>
    tx.status === activeTab && (activeAcquirer === 'Todos' || tx.acquirer === activeAcquirer)
  );

  const total = filtered.reduce((s, t) => s + t.value, 0);
  const avgFee = filtered.length ? (filtered.reduce((s, t) => s + t.feeRaw, 0) / filtered.length).toFixed(2) : '0.00';
  const divergencias = filtered.filter(t => t.feeRaw > 2).reduce((s, t) => s + t.value * (t.feeRaw - 2) / 100, 0);

  const iconEl = (icon: string, hovering?: boolean) => {
    if (icon === 'pix') return <PixIcon size={18} />;
    return <CreditCard size={15} />;
  };

  return (
    <div className="les-mockup-container" style={{ background: 'transparent', width: '100%', maxWidth: '1100px', padding: '16px', display: 'flex', alignItems: 'flex-start', gap: '20px' }}>

      <div className="relative flex-1">
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="bg-white rounded-[2.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.25)] border border-slate-100 overflow-hidden w-full"
        >
          {/* Header */}
          <div className="bg-[#7f00ff] px-6 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white rounded-2xl flex items-center justify-center shadow-md overflow-hidden">
                <img src="/logo.png" alt="Lukos" className="w-6 h-6 object-contain" />
              </div>
              <div>
                <h4 className="text-white font-black text-sm tracking-tight">Lukos Conciliação</h4>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[10px] text-white/70 font-bold uppercase tracking-widest">Sincronizado</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-white/10 p-1.5 rounded-xl border border-white/10 text-white/70">
                <Calendar size={14} />
              </div>
              <div className="bg-white/10 px-3 py-1.5 rounded-xl border border-white/10 text-white text-[11px] font-bold flex items-center gap-1">
                Abril 2026 <ChevronDown size={12} className="opacity-50" />
              </div>
            </div>
          </div>

          {/* Summary Row — dynamic */}
          <div className="grid grid-cols-3 gap-0 px-6 py-2 bg-slate-50/60 border-b border-slate-100">
            <div className="px-2 py-1.5">
              <p className="text-[9px] font-black text-slate-400 uppercase mb-0.5">Total</p>
              <motion.h5 key={total} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-sm font-black text-slate-900 tracking-tighter">
                R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </motion.h5>
            </div>
            <div className="px-2 py-1.5 border-x border-slate-100">
              <p className="text-[9px] font-black text-slate-400 uppercase mb-0.5">Taxa média</p>
              <motion.h5 key={avgFee} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="text-sm font-black text-[#7f00ff] tracking-tighter">
                {avgFee}%
              </motion.h5>
            </div>
            <div className="px-2 py-1.5">
              <p className="text-[9px] font-black text-slate-400 uppercase mb-0.5">Divergências</p>
              <motion.h5 key={divergencias} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className={`text-sm font-black tracking-tighter ${divergencias > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {divergencias > 0 ? `R$ ${divergencias.toFixed(2)}` : '✓ Nenhuma'}
              </motion.h5>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="flex items-center gap-2 px-6 py-2.5 border-b border-slate-100 flex-wrap">
            {/* Status tabs */}
            <div className="flex bg-slate-100 p-0.5 rounded-xl">
              {(['Pendente', 'Conciliado'] as const).map(t => (
                <button key={t} onClick={() => setActiveTab(t)}
                  className={`px-3 py-1 rounded-lg text-[11px] font-black transition-all ${activeTab === t ? 'bg-white shadow-sm text-[#7f00ff]' : 'text-slate-400 hover:text-slate-600'}`}>
                  {t}
                </button>
              ))}
            </div>

            {/* Acquirer pills */}
            <div className="flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded-xl border border-slate-100">
              <span className="text-[9px] font-black text-slate-400 uppercase px-1">Adquirente</span>
              {acquirers.map(name => (
                <button key={name} onClick={() => setActiveAcquirer(name)}
                  className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold border transition-all ${activeAcquirer === name ? 'bg-[#7f00ff] text-white border-[#7f00ff] shadow-sm shadow-purple-200' : 'bg-white text-slate-500 border-slate-200 hover:border-purple-200 hover:text-[#7f00ff]'}`}>
                  {name}
                </button>
              ))}
            </div>

            {/* Filter icon */}
            <button onClick={() => setFilterOpen(f => !f)}
              className={`ml-auto w-7 h-7 flex items-center justify-center rounded-lg border transition-all ${filterOpen ? 'bg-[#7f00ff] border-[#7f00ff] text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-purple-300'}`}>
              <Filter size={13} />
            </button>
          </div>

          {/* Extra filter row (toggleable) */}
          {filterOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
              className="px-7 py-3 bg-purple-50/50 border-b border-purple-100 flex gap-3 flex-wrap items-center">
              <span className="text-[9px] font-black text-[#7f00ff] uppercase tracking-widest">Filtros avançados:</span>
              {['Apenas Pix', 'Taxa &gt; 2%', 'Hoje', 'Esta semana'].map(f => (
                <button key={f} className="px-3 py-1 rounded-full text-[10px] font-bold border border-purple-200 bg-white text-slate-600 hover:bg-[#7f00ff] hover:text-white hover:border-[#7f00ff] transition-all">
                  {f}
                </button>
              ))}
            </motion.div>
          )}

          {/* Transaction list */}
          <div className="px-4 py-3 space-y-1.5">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-slate-400">
                <CheckCircle size={24} className="mb-1.5 text-slate-300" />
                <p className="text-[12px] font-bold">Nenhuma transação encontrada</p>
              </div>
            ) : filtered.map((row, i) => (
              <motion.div
                key={row.id}
                initial={{ x: -16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: i * 0.07 }}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-white border border-slate-100 hover:border-[#7f00ff]/30 hover:shadow-md hover:shadow-purple-500/5 transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 group-hover:text-[#7f00ff] group-hover:bg-purple-50 transition-colors shrink-0">
                    {iconEl(row.icon)}
                  </div>
                  <div>
                    <h6 className="text-[12px] font-black text-slate-900 leading-none mb-0.5">{row.source}</h6>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{row.id} · {row.date}</p>
                  </div>
                </div>
                <div className="text-right mr-3">
                  <h6 className="text-[12px] font-black text-slate-900 leading-none mb-0.5">R$ {row.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h6>
                  <p className={`text-[9px] font-bold uppercase ${row.feeRaw > 2 ? 'text-red-400' : 'text-emerald-500'}`}>Taxa {row.fee}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 rounded-lg text-[8px] font-black uppercase ${row.status === 'Conciliado' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-500'}`}>
                    {row.status}
                  </span>
                  <div className="w-5 h-5 flex items-center justify-center text-slate-200 group-hover:text-[#7f00ff] transition-colors">
                    <ArrowRight size={12} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>


        </motion.div>
      </div>



    </div>
  );
};


export const LukIAMockup = () => {
  const [step, setStep] = React.useState(0);

  const quickOptions = [
    { label: '📦 Cadastrar produto', next: 1 },
    { label: '📋 Ver meus produtos', next: 7 },
    { label: '📊 Analisar estoque', next: 2 },
    { label: '📈 Gráfico de vendas', next: 8 },
    { label: '💰 Auditar taxas', next: 3 },
  ];

  type ChatStep = { question?: string; answer: string; isConfirmation?: boolean; chips?: { label: string; next: number }[] };
  const chatFlows: Record<number, ChatStep> = {
    // Flow 1: Cadastrar produto
    1: {
      question: '📦 Cadastrar produto',
      answer: 'Produto identificado: **Óleo Motor 5W-30 1L (Lubrax)**. NCM 2710.19.31, ICMS-ST aplicado automaticamente. Deseja salvar?',
      chips: [
        { label: '✅ Confirmar cadastro', next: 4 },
        { label: '✏️ Editar dados', next: 0 },
        { label: '🔄 Outro produto', next: 0 },
      ],
    },
    4: {
      answer: '✅ **Produto cadastrado com sucesso!** Estoque inicial definido como 0 unidades. Código interno: **PRD-00412**.',
      isConfirmation: true,
      chips: [
        { label: '📦 Cadastrar outro', next: 1 },
        { label: '🏠 Voltar ao início', next: 0 },
      ],
    },

    // Flow 2: Analisar estoque
    2: {
      question: '📊 Analisar estoque',
      answer: 'Analisando 847 itens da conveniência... **4 produtos abaixo** do estoque mínimo: Água 500ml, Óleo 5W-30, Snack X e Aditivo Arrefecedor.',
      chips: [
        { label: '📋 Ver relatório completo', next: 5 },
        { label: '🏠 Voltar ao início', next: 0 },
      ],
    },
    5: {
      answer: '📋 Relatório gerado com sucesso! **4 itens críticos** — perda estimada de vendas: **R$ 1.240,00/semana** se não reposto.',
      isConfirmation: true,
      chips: [
        { label: '📊 Nova análise', next: 2 },
        { label: '🏠 Voltar ao início', next: 0 },
      ],
    },

    // Flow 3: Auditar taxas
    3: {
      question: '💰 Auditar taxas',
      answer: 'Auditoria concluída na Stone. Encontrei **R$ 840/mês** em cobranças de taxas acima do contratado no plano Débito.',
      chips: [
        { label: '📄 Exportar relatório', next: 6 },
        { label: '🏠 Voltar ao início', next: 0 },
      ],
    },
    6: {
      answer: '📄 **Relatório exportado!** Arquivo PDF enviado para o e-mail do gerente. Use para contestar junto à Stone.',
      isConfirmation: true,
      chips: [
        { label: '💰 Nova auditoria', next: 3 },
        { label: '🏠 Voltar ao início', next: 0 },
      ],
    },
    // Flow 7: Ver meus produtos (table view)
    7: {
      question: '📋 Ver meus produtos',
      answer: '',
      chips: [
        { label: '📦 Cadastrar produto', next: 1 },
        { label: '📊 Analisar estoque', next: 2 },
        { label: '🏠 Voltar ao início', next: 0 },
      ],
    },
    // Flow 8: Gráfico de vendas
    8: {
      question: '📈 Gráfico de vendas',
      answer: '',
      chips: [
        { label: '📊 Analisar estoque', next: 2 },
        { label: '💰 Auditar taxas', next: 3 },
        { label: '🏠 Voltar ao início', next: 0 },
      ],
    },
  };

  const productTable = [
    { name: 'Óleo 5W-30 1L', cat: 'Lubrificantes', qty: 48, min: 30, status: 'ok' },
    { name: 'Água Mineral 500ml', cat: 'Conveniência', qty: 12, min: 50, status: 'low' },
    { name: 'Aditivo Arrefecedor', cat: 'Lubrificantes', qty: 9, min: 20, status: 'low' },
    { name: 'Café 200ml', cat: 'Conveniência', qty: 34, min: 20, status: 'ok' },
  ];

  const salesChart = [
    { day: 'Seg', value: 3200, pct: 64 },
    { day: 'Ter', value: 4100, pct: 82 },
    { day: 'Qua', value: 2800, pct: 56 },
    { day: 'Qui', value: 4800, pct: 96 },
    { day: 'Sex', value: 5000, pct: 100 },
    { day: 'Sáb', value: 4300, pct: 86 },
    { day: 'Dom', value: 2200, pct: 44 },
  ];

  const current = step > 0 ? chatFlows[step] : null;
  const userMessage = current?.question ?? (
    step === 4 ? '✅ Confirmar cadastro' :
    step === 5 ? '📋 Ver relatório completo' :
    step === 6 ? '📄 Exportar relatório' : null
  );

  return (
    <div className="les-mockup-container" style={{
      background: 'linear-gradient(135deg, #7f00ff 0%, #5b21b6 50%, #3b82f6 100%)',
      width: '100%', maxWidth: '580px', padding: '28px',
      boxShadow: '0 40px 80px rgba(127,0,255,0.35)'
    }}>
      <div style={{ position:'absolute', top:'-20px', right:'-20px', width:'140px', height:'140px', borderRadius:'50%', background:'rgba(255,255,255,0.08)', filter:'blur(30px)', pointerEvents:'none' }} />

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 bg-white rounded-[2rem] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.3)] flex flex-col"
        style={{ minHeight: '420px' }}
      >
        {/* Header */}
        <div className="bg-[#7f00ff] px-5 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-9 h-9 bg-white rounded-2xl flex items-center justify-center shadow-md overflow-hidden">
                <img src="/logo.png" alt="Lukos" className="w-7 h-7 object-contain" />
              </div>
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-[#7f00ff]" />
            </div>
            <div>
              <p className="text-white font-black text-sm leading-none">LukIA Assistant</p>
              <p className="text-white/60 text-[9px] font-bold uppercase tracking-widest mt-0.5">Sempre disponível</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full border border-white/20">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[9px] text-white font-bold">Online</span>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 p-4 space-y-3 overflow-hidden bg-slate-50/50">

          {/* Initial AI message */}
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-xl bg-white flex items-center justify-center shrink-0 mt-0.5 shadow-md shadow-purple-200 overflow-hidden border border-purple-100">
              <img src="/logo.png" alt="LukIA" className="w-5 h-5 object-contain" />
            </div>
            <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 shadow-sm border border-slate-100 max-w-[85%]">
              <p className="text-[12px] font-semibold text-slate-700 leading-relaxed">
                Olá! Sou a <span className="text-[#7f00ff] font-black">LukIA</span>. Como posso te ajudar hoje?
              </p>
            </div>
          </div>

          {/* Quick Action Chips (initial state) */}
          {step === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col gap-2 pl-9"
            >
              {quickOptions.map((opt, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  onClick={() => setStep(opt.next)}
                  className="text-left px-4 py-2.5 bg-white border-2 border-purple-100 hover:border-[#7f00ff] hover:bg-purple-50 rounded-2xl text-[12px] font-bold text-slate-700 transition-all duration-200 hover:text-[#7f00ff] group"
                >
                  <span className="group-hover:translate-x-1 inline-block transition-transform">{opt.label}</span>
                </motion.button>
              ))}
            </motion.div>
          )}

          {/* User's selected option bubble */}
          {current && userMessage && (
            <motion.div
              key={`user-${step}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex justify-end"
            >
              <div className="bg-[#7f00ff] text-white rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[75%] shadow-md shadow-purple-200">
                <p className="text-[12px] font-semibold">{userMessage}</p>
              </div>
            </motion.div>
          )}

          {/* AI Response */}
          {current && (
            <motion.div
              key={`ai-${step}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-start gap-2"
            >
              <div className="w-7 h-7 rounded-xl bg-white flex items-center justify-center shrink-0 mt-0.5 shadow-md shadow-purple-200 overflow-hidden border border-purple-100">
                <img src="/logo.png" alt="LukIA" className="w-5 h-5 object-contain" />
              </div>

              {/* Chart View */}
              {step === 8 ? (
                <div className="flex-1 bg-white rounded-2xl rounded-tl-md shadow-sm border border-slate-100 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Vendas — Últimos 7 dias</span>
                    <span className="text-[9px] font-bold text-[#7f00ff] bg-purple-50 px-2 py-0.5 rounded-full">+18% vs semana ant.</span>
                  </div>
                  {/* Summary row */}
                  <div className="flex gap-3 px-3 pt-2 pb-1 border-b border-slate-50">
                    <div>
                      <p className="text-[7px] font-black text-slate-400 uppercase">Total</p>
                      <p className="text-[11px] font-black text-slate-900">R$ 26.400</p>
                    </div>
                    <div>
                      <p className="text-[7px] font-black text-slate-400 uppercase">Melhor dia</p>
                      <p className="text-[11px] font-black text-[#7f00ff]">Sex — R$ 5.000</p>
                    </div>
                    <div>
                      <p className="text-[7px] font-black text-slate-400 uppercase">Ticket médio</p>
                      <p className="text-[11px] font-black text-slate-900">R$ 87,40</p>
                    </div>
                  </div>
                  {/* Bar chart */}
                  <div className="px-3 pt-3 pb-2 flex items-end gap-1.5 h-[90px]">
                    {salesChart.map((bar, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full relative flex items-end" style={{ height: '60px' }}>
                          <motion.div
                            className="w-full rounded-t-md"
                            style={{ background: bar.pct === 100 ? '#7f00ff' : bar.pct >= 80 ? '#a855f7' : '#e9d5ff' }}
                            initial={{ height: 0 }}
                            animate={{ height: `${bar.pct}%` }}
                            transition={{ delay: 0.3 + i * 0.07, duration: 0.5, ease: 'easeOut' }}
                          />
                        </div>
                        <span className="text-[7px] font-black text-slate-400">{bar.day}</span>
                      </div>
                    ))}
                  </div>
                  <div className="px-3 py-1.5 border-t border-slate-100 flex justify-end">
                    <span className="text-[8px] font-black text-[#7f00ff] cursor-pointer hover:underline">Ver relatório completo →</span>
                  </div>
                </div>
              ) : step === 7 ? (
                <div className="flex-1 bg-white rounded-2xl rounded-tl-md shadow-sm border border-slate-100 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Produtos Cadastrados</span>
                    <span className="text-[9px] font-bold text-[#7f00ff] bg-purple-50 px-2 py-0.5 rounded-full">847 total</span>
                  </div>
                  {/* Table Header */}
                  <div className="grid grid-cols-4 gap-0 px-3 pt-2 pb-1 border-b border-slate-50">
                    {['Produto', 'Categ.', 'Qtd', 'Status'].map(h => (
                      <span key={h} className="text-[8px] font-black text-slate-400 uppercase">{h}</span>
                    ))}
                  </div>
                  {/* Table Rows */}
                  {productTable.map((row, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      className={`grid grid-cols-4 gap-0 px-3 py-2 items-center ${
                        i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                      } hover:bg-purple-50/30 transition-colors cursor-pointer`}
                    >
                      <span className="text-[9px] font-bold text-slate-800 truncate pr-1">{row.name}</span>
                      <span className="text-[8px] text-slate-400 font-medium truncate">{row.cat}</span>
                      <span className={`text-[9px] font-black ${ row.status === 'low' ? 'text-red-500' : 'text-slate-700' }`}>{row.qty} un.</span>
                      <span className={`text-[7px] font-black px-1.5 py-0.5 rounded-full text-center ${
                        row.status === 'low'
                          ? 'bg-red-50 text-red-500'
                          : 'bg-emerald-50 text-emerald-600'
                      }`}>
                        {row.status === 'low' ? '⚠ Baixo' : '✓ OK'}
                      </span>
                    </motion.div>
                  ))}
                  <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[8px] text-slate-400 font-medium">Mostrando 4 de 847 produtos</span>
                    <span className="text-[8px] font-black text-[#7f00ff] cursor-pointer hover:underline">Ver todos →</span>
                  </div>
                </div>
              ) : (
                <div className={`rounded-2xl rounded-tl-md px-4 py-3 shadow-sm border max-w-[85%] ${current.isConfirmation ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-slate-100'}`}>
                  <p className="text-[12px] font-semibold text-slate-700 leading-relaxed">
                    {current.answer.split('**').map((part, i) =>
                      i % 2 === 1
                        ? <strong key={i} className={current.isConfirmation ? 'text-emerald-600' : 'text-[#7f00ff]'}>{part}</strong>
                        : part
                    )}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Follow-up chips */}
          {current?.chips && (
            <motion.div
              key={`chips-${step}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-2 pl-9"
            >
              {current.chips.map((chip, i) => (
                <button
                  key={i}
                  onClick={() => setStep(chip.next)}
                  className="px-3 py-1.5 bg-purple-50 hover:bg-[#7f00ff] border border-purple-200 hover:border-[#7f00ff] text-[11px] font-bold text-[#7f00ff] hover:text-white rounded-full transition-all duration-200"
                >
                  {chip.label}
                </button>
              ))}
            </motion.div>
          )}
        </div>

        {/* Input Bar */}
        <div className="px-4 py-3 border-t border-slate-100 bg-white flex items-center gap-2 shrink-0">
          <div className="flex-1 bg-slate-50 rounded-xl px-3 py-2 border border-slate-100 flex items-center">
            <span className="text-[11px] text-slate-400 font-medium">Pergunte algo à LukIA...</span>
          </div>
          <button className="w-8 h-8 bg-[#7f00ff] rounded-xl flex items-center justify-center shadow-md shadow-purple-200 hover:scale-105 transition-transform">
            <ArrowRight size={14} className="text-white" />
          </button>
        </div>
      </motion.div>
    </div>
  );
};


export const LukosPayMockup = () => (
  <div className="les-mockup-container" style={{ background: 'linear-gradient(135deg, #0f172a, #334155)', width: '100%', maxWidth: '600px' }}>
    <div className="les-window">
      <div className="les-window-header">
        <div className="les-dot" /> <div className="les-dot" /> <div className="les-dot" />
      </div>
      <div className="les-window-body">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="text-[10px] text-slate-400 font-bold uppercase mb-1">Saldo Lukos Pay</div>
            <div className="text-2xl font-black text-slate-900">R$ 12.540,00</div>
          </div>
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center text-white">
            <CreditCard size={24} />
          </div>
        </div>
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-emerald-500 shadow-sm">
                  <CheckCircle size={16} />
                </div>
                <div>
                  <div className="text-[11px] font-bold text-slate-800">Venda #904{i}</div>
                  <div className="text-[9px] text-slate-400">Cartão de Crédito</div>
                </div>
              </div>
              <div className="text-sm font-black text-slate-900">R$ 145,90</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="les-floating-card md" style={{ top: '60px', left: '-20px' }}>
      <div className="flex items-center gap-2 text-slate-900 mb-2">
        <Shield size={16} className="text-emerald-500" />
        <span className="text-[11px] font-black uppercase">Antifraude Ativo</span>
      </div>
      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 w-[95%]" />
      </div>
    </div>
  </div>
);

export const PriceManagementMockup = () => {
  const [prices, setPrices] = React.useState({
    comum: 5.89,
    aditivada: 6.09,
    diesel: 5.95,
  });
  const [syncing, setSyncing] = React.useState(false);

  const handleUpdate = () => {
    setSyncing(true);
    setTimeout(() => setSyncing(false), 2000);
  };

  return (
    <div className="les-mockup-container" style={{ background: 'transparent', width: '100%', maxWidth: '300px', padding: 0 }}>
      <div className="relative bg-[#1a1a1a] rounded-[3rem] p-3 border-[6px] border-[#333] shadow-2xl overflow-hidden aspect-[9/19]">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#333] rounded-b-2xl z-20" />
        
        {/* App UI */}
        <div className="bg-slate-50 h-full rounded-[2.2rem] overflow-hidden flex flex-col font-sans">
          <div className="bg-[#7f00ff] px-4 pt-8 pb-4 text-white">
            <p className="text-[10px] font-bold opacity-70 uppercase tracking-widest">Posto Lukos Centro</p>
            <h4 className="text-lg font-black tracking-tight">Gestão de Preços</h4>
          </div>

          <div className="p-4 flex-1 space-y-4">
            <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[11px] font-black text-slate-400 uppercase">Gasolina Comum</span>
                <TrendingUp size={12} className="text-emerald-500" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-black text-slate-800">R$ {prices.comum.toFixed(2)}</span>
                <div className="flex gap-1">
                  <button onClick={() => setPrices(p => ({...p, comum: p.comum - 0.01}))} className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded-lg text-slate-600 font-bold">-</button>
                  <button onClick={() => setPrices(p => ({...p, comum: p.comum + 0.01}))} className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded-lg text-slate-600 font-bold">+</button>
                </div>
              </div>
            </div>

            <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[11px] font-black text-slate-400 uppercase">Gasolina Aditivada</span>
                <TrendingUp size={12} className="text-emerald-500" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-black text-slate-800">R$ {prices.aditivada.toFixed(2)}</span>
                <div className="flex gap-1">
                  <button onClick={() => setPrices(p => ({...p, aditivada: p.aditivada - 0.01}))} className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded-lg text-slate-600 font-bold">-</button>
                  <button onClick={() => setPrices(p => ({...p, aditivada: p.aditivada + 0.01}))} className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded-lg text-slate-600 font-bold">+</button>
                </div>
              </div>
            </div>

            <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[11px] font-black text-slate-400 uppercase">Diesel S10</span>
                <TrendingUp size={12} className="text-slate-400" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xl font-black text-slate-800">R$ {prices.diesel.toFixed(2)}</span>
                <div className="flex gap-1">
                  <button onClick={() => setPrices(p => ({...p, diesel: p.diesel - 0.01}))} className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded-lg text-slate-600 font-bold">-</button>
                  <button onClick={() => setPrices(p => ({...p, diesel: p.diesel + 0.01}))} className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded-lg text-slate-600 font-bold">+</button>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-slate-100 bg-white">
            <button 
              onClick={handleUpdate}
              disabled={syncing}
              className={`w-full py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${syncing ? 'bg-emerald-500 text-white' : 'bg-[#7f00ff] text-white shadow-lg shadow-purple-200 hover:scale-[1.02]'}`}
            >
              {syncing ? 'Sincronizando Bombas...' : 'Atualizar Preços'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PDVMovelMockup = () => (

  <div className="les-mockup-container" style={{ background: 'transparent', width: '100%', maxWidth: '340px', padding: 0 }}>
    <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.2)]">
      <img
        src="/smartpos.jpg"
        alt="PDV Móvel"
        className="w-full h-auto object-cover transition-transform hover:scale-[1.03] duration-500"
        style={{ maxHeight: '360px', objectPosition: 'center top' }}
      />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(127,0,255,0.15) 0%, transparent 60%)' }} />
    </div>
  </div>
);
